import React from 'react';
import {
  makeStyles,
  shorthands,
  tokens,
  Switch,
  Text,
  Divider,
  Badge,
  Spinner,
  Button,
  Link as FluentLink,
} from '@fluentui/react-components';
import { ArrowClockwise20Regular, Open16Regular } from '@fluentui/react-icons';
import { useQuery } from '@tanstack/react-query';
import { useDebug } from '../contexts/DebugContext';
import {
  fetchVercelStatus,
  IN_FLIGHT_STATES,
  type VercelDeployment,
} from '../services/vercelStatusService';

const useStyles = makeStyles({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    ...shorthands.padding('24px'),
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('4px'),
    boxShadow: tokens.shadow8,
    ...shorthands.padding('20px', '24px'),
  },
  sectionTitle: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorNeutralForeground1,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.gap('12px'),
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.padding('8px', '0'),
  },
  rowLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  rowDescription: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  // Deployment list
  deployRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    ...shorthands.gap('16px'),
    ...shorthands.padding('10px', '0'),
  },
  deployMeta: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
  },
  deployTitle: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
    fontSize: tokens.fontSizeBase300,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  deploySub: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    flexWrap: 'wrap',
  },
  mono: {
    fontFamily: 'monospace',
  },
  deployState: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
    flexShrink: 0,
  },
  muted: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
});

type BadgeColor = React.ComponentProps<typeof Badge>['color'];

const STATE_META: Record<string, { label: string; color: BadgeColor }> = {
  ready: { label: 'Ready', color: 'success' },
  building: { label: 'Building', color: 'informative' },
  queued: { label: 'Queued', color: 'informative' },
  error: { label: 'Error', color: 'danger' },
  canceled: { label: 'Canceled', color: 'warning' },
  skipped: { label: 'Skipped', color: 'subtle' },
  blocked: { label: 'Blocked', color: 'warning' },
  rolled_back: { label: 'Rolled back', color: 'warning' },
  cleaned_up: { label: 'Cleaned up', color: 'subtle' },
  unknown: { label: 'Unknown', color: 'subtle' },
};

const envLabel = (d: VercelDeployment): string => {
  const raw = d.custom_environment || d.target || 'preview';
  return raw.charAt(0).toUpperCase() + raw.slice(1);
};

const relTime = (iso: string | null): string => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const DeploymentRow: React.FC<{ d: VercelDeployment }> = ({ d }) => {
  const styles = useStyles();
  const meta = STATE_META[d.state] || STATE_META.unknown;
  const isBuilding = IN_FLIGHT_STATES.has(d.state);

  return (
    <div className={styles.deployRow}>
      <div className={styles.deployMeta}>
        <div className={styles.deployTitle}>
          <span>{d.project || 'Unknown project'}</span>
          <Badge appearance="tint" color="brand" size="small">
            {envLabel(d)}
          </Badge>
        </div>
        <div className={styles.deploySub}>
          {d.branch && <span className={styles.mono}>{d.branch}</span>}
          {d.sha && <span className={styles.mono}>{d.sha}</span>}
          {d.url && (
            <FluentLink href={`https://${d.url}`} target="_blank" rel="noopener noreferrer">
              open <Open16Regular />
            </FluentLink>
          )}
        </div>
      </div>
      <div className={styles.deployState}>
        <Text className={styles.muted}>{relTime(d.updated_at)}</Text>
        {isBuilding && <Spinner size="extra-tiny" />}
        <Badge appearance="filled" color={meta.color}>
          {meta.label}
        </Badge>
      </div>
    </div>
  );
};

const DeploymentsSection: React.FC = () => {
  const styles = useStyles();
  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['vercel-status'],
    queryFn: fetchVercelStatus,
    staleTime: 0,
    // Idle at rest; poll only while something is mid-build.
    refetchInterval: (query) => {
      const deps = query.state.data as VercelDeployment[] | undefined;
      return deps?.some((d) => IN_FLIGHT_STATES.has(d.state)) ? 5000 : false;
    },
  });

  const deployments = data || [];

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader}>
        <Text className={styles.sectionTitle}>Deployments</Text>
        <Button
          appearance="subtle"
          size="small"
          icon={isFetching ? <Spinner size="extra-tiny" /> : <ArrowClockwise20Regular />}
          onClick={() => refetch()}
          disabled={isFetching}
        >
          Refresh
        </Button>
      </div>
      <Text className={styles.rowDescription}>
        Live deployment state from Vercel webhooks (source of truth: prod backend). Polls only while a build is in flight.
      </Text>
      <Divider />
      {isLoading ? (
        <Spinner size="tiny" label="Loading deployments…" />
      ) : isError ? (
        <Text className={styles.muted}>
          Couldn&apos;t reach the deployment status endpoint. It may not be deployed to prod yet.
        </Text>
      ) : deployments.length === 0 ? (
        <Text className={styles.muted}>
          No deployment events recorded yet. Trigger a deploy to populate this.
        </Text>
      ) : (
        deployments.map((d) => <DeploymentRow key={d.deployment_id} d={d} />)
      )}
    </div>
  );
};

const DevSettings: React.FC = () => {
  const styles = useStyles();
  const { debugMode, setDebugMode, tanstackDevtools, setTanstackDevtools } = useDebug();

  return (
    <div className={styles.content}>
      <DeploymentsSection />

      <div className={styles.section}>
        <Text className={styles.sectionTitle}>General</Text>
        <Divider />
        <div className={styles.row}>
          <div className={styles.rowLabel}>
            <Text>Debug Mode</Text>
            <Text className={styles.rowDescription}>
              Enables debug panels, API previews, and verbose logging
            </Text>
          </div>
          <Switch
            checked={debugMode}
            onChange={(_, data) => setDebugMode(data.checked)}
          />
        </div>
        <div className={styles.row}>
          <div className={styles.rowLabel}>
            <Text>TanStack Query DevTools</Text>
            <Text className={styles.rowDescription}>
              Shows the React Query inspector icon in the bottom corner
            </Text>
          </div>
          <Switch
            checked={tanstackDevtools}
            onChange={(_, data) => setTanstackDevtools(data.checked)}
          />
        </div>
      </div>
    </div>
  );
};

export default DevSettings;
